import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useApp } from "@/context/AppContext";
import { Trash2, Plus, Download, AlertTriangle } from "lucide-react";

const fmt = (v) =>
  new Intl.NumberFormat("fr-LU", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);

const DashboardPage = () => {
  const { t, lang, user, API, authHeaders, logout } = useApp();
  const nav = useNavigate();
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (user === false) nav("/login");
  }, [user, nav]);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/scenarios`, authHeaders());
      setScenarios(res.data);
    } catch (err) {
      console.error("Failed to load scenarios", err);
    } finally {
      setLoading(false);
    }
  }, [API, authHeaders]);

  useEffect(() => { if (user) load(); }, [user, load]);

  const del = async (id) => {
    try {
      await axios.delete(`${API}/scenarios/${id}`, authHeaders());
      toast.success("Supprimé");
      setScenarios((s) => s.filter((x) => x.id !== id));
    } catch (err) {
      console.error("Failed to delete scenario", err);
      toast.error("Erreur");
    }
  };

  const labels = {
    fr: { gdpr: "Mes données — RGPD", export: "Exporter mes données", del: "Supprimer mon compte",
          delDesc: "Supprime définitivement votre compte, tous vos scénarios et toutes données liées. Cette action est irréversible.",
          delConfirm: "Cette action est IRRÉVERSIBLE. Tapez SUPPRIMER pour confirmer la suppression définitive de votre compte et de toutes vos données.",
          delWord: "SUPPRIMER", delOk: "Compte et données supprimés.", expOk: "Données exportées." },
    en: { gdpr: "My data — GDPR", export: "Export my data", del: "Delete my account",
          delDesc: "Permanently deletes your account, all your scenarios and any linked data. This action is irreversible.",
          delConfirm: "This action is IRREVERSIBLE. Type DELETE to confirm permanent deletion of your account and all data.",
          delWord: "DELETE", delOk: "Account and data deleted.", expOk: "Data exported." },
    de: { gdpr: "Meine Daten — DSGVO", export: "Daten exportieren", del: "Konto löschen",
          delDesc: "Löscht Ihr Konto, alle Szenarien und alle damit verbundenen Daten endgültig. Diese Aktion ist unwiderruflich.",
          delConfirm: "Diese Aktion ist UNWIDERRUFLICH. Geben Sie LÖSCHEN ein, um die endgültige Löschung zu bestätigen.",
          delWord: "LÖSCHEN", delOk: "Konto und Daten gelöscht.", expOk: "Daten exportiert." },
  }[lang] || {};

  const exportData = async () => {
    try {
      const res = await axios.get(`${API}/auth/export`, authHeaders());
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `lux-pension-export-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(labels.expOk);
    } catch (err) {
      console.error("Export failed", err);
      toast.error("Export error");
    }
  };

  const deleteAccount = async () => {
    const answer = window.prompt(labels.delConfirm);
    if (answer !== labels.delWord) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/account`, authHeaders());
      toast.success(labels.delOk);
      await logout();
      nav("/");
    } catch (err) {
      console.error("Account deletion failed", err);
      toast.error("Deletion error");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="max-w-7xl mx-auto px-6 md:px-8 py-16" data-testid="dashboard-page">
      <div className="flex items-end justify-between border-b border-slate-200 pb-6 mb-8">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500">/ {t.dashboard.compareLabel}</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-slate-900 mt-2">{t.dashboard.title}</h1>
        </div>
        <Link to="/" data-testid="new-scenario-btn"
          className="px-5 py-2.5 bg-slate-900 text-white text-xs font-mono uppercase tracking-[0.15em] hover:bg-slate-800 inline-flex items-center gap-2">
          <Plus size={14} strokeWidth={1.5} /> {t.dashboard.newScenario}
        </Link>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500 font-mono">…</div>
      ) : scenarios.length === 0 ? (
        <div className="bg-white border border-slate-200 p-12 text-center" data-testid="dashboard-empty">
          <div className="font-serif text-2xl text-slate-300 mb-3">—</div>
          <p className="text-sm text-slate-500">{t.dashboard.empty}</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200">
          <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-slate-200 bg-slate-50 text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">
            <div className="col-span-3">Label</div>
            <div className="col-span-1 text-right">Method</div>
            <div className="col-span-1 text-right">Class</div>
            <div className="col-span-2 text-right">Gross / mo</div>
            <div className="col-span-2 text-right">Net / mo</div>
            <div className="col-span-2 text-right">Replacement</div>
            <div className="col-span-1 text-right">Action</div>
          </div>
          {scenarios.map((s) => (
            <div key={s.id} data-testid={`scenario-row-${s.id}`}
              className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 transition-colors items-center">
              <div className="col-span-3">
                <div className="font-serif text-base text-slate-900">{s.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-slate-400 mt-1">
                  {t.dashboard.created} {new Date(s.created_at).toLocaleDateString()}
                </div>
              </div>
              <div className="col-span-1 text-right text-xs font-mono uppercase text-slate-600">{s.result.method}</div>
              <div className="col-span-1 text-right text-xs font-mono text-slate-600">{s.result.tax_class}</div>
              <div className="col-span-2 text-right font-mono tabular text-sm text-slate-900">{fmt(s.result.gross_monthly_pension)}</div>
              <div className="col-span-2 text-right font-mono tabular text-sm font-medium text-blue-700">{fmt(s.result.net_monthly_pension)}</div>
              <div className="col-span-2 text-right font-mono tabular text-sm text-slate-900">{s.result.replacement_rate}%</div>
              <div className="col-span-1 text-right">
                <button onClick={() => del(s.id)} data-testid={`delete-${s.id}`}
                  className="p-2 text-slate-400 hover:text-red-700">
                  <Trash2 size={14} strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* GDPR self-service controls */}
      <div className="mt-16 border border-slate-200 bg-white p-6 md:p-8" data-testid="gdpr-section">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-500 mb-2">/ rgpd · gdpr · dsgvo</div>
        <h2 className="font-serif text-2xl text-slate-900 mb-6">{labels.gdpr}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={exportData} data-testid="export-data-btn"
            className="px-5 py-3 bg-white border border-slate-300 text-slate-900 text-xs font-mono uppercase tracking-[0.15em] hover:bg-slate-50 inline-flex items-center justify-center gap-2">
            <Download size={14} strokeWidth={1.5} />
            {labels.export}
          </button>
          <button onClick={deleteAccount} disabled={deleting} data-testid="delete-account-btn"
            className="px-5 py-3 bg-white border border-red-300 text-red-700 text-xs font-mono uppercase tracking-[0.15em] hover:bg-red-50 disabled:opacity-50 inline-flex items-center justify-center gap-2">
            <AlertTriangle size={14} strokeWidth={1.5} />
            {labels.del}
          </button>
        </div>
        <p className="text-xs text-slate-500 mt-4 leading-relaxed">{labels.delDesc}</p>
        <Link to="/privacy" data-testid="link-privacy" className="block mt-3 text-[11px] font-mono uppercase tracking-[0.15em] text-blue-700 hover:underline">
          → {t.nav.privacy}
        </Link>
      </div>
    </section>
  );
};

export default DashboardPage;
